import type { Card } from "../types";

export const cardsData: { cards: Card[] } = {
  cards: [
    {
      id: "c1",
      problem: "Horizontal Banding",
      category: "print-quality",
      severity: "high",
      cause: [
        "Partial nozzle loss from clogs in one or more channels.",
        "Dirty encoder strip causing carriage timing errors.",
        "Ink starvation from air in lines, weak dampers, or restricted flow.",
        "Pass count too low or ink limits too conservative for the artwork.",
        "Mechanical instability after a head strike or carriage issue."
      ],
      fix: [
        "Run a nozzle check first and identify which channels are missing or unstable.",
        "Clean the encoder strip carefully with lint-free material and proper cleaner.",
        "Inspect carts, lines, dampers, and filters for air, restriction, or poor ink flow.",
        "Increase pass count or review ink limits if the print is too light in solid areas.",
        "If the issue started after a strike, inspect carriage path, head height, and alignment."
      ]
    },
    {
      id: "c2",
      problem: "Vertical Banding",
      category: "print-quality",
      severity: "high",
      cause: [
        "Bi-directional alignment drift.",
        "Dirty or damaged encoder strip.",
        "Media advance inconsistency or film tension issue.",
        "Incorrect feed compensation in the RIP."
      ],
      fix: [
        "Run the full bi-di alignment procedure and confirm the best pattern, not just a quick guess.",
        "Clean the encoder strip and inspect for scratches or staining.",
        "Check film tracking, tension, and feed consistency across the platen.",
        "Review RIP feed and step settings if the issue appears repeatedly on the same jobs."
      ]
    },
    {
      id: "c3",
      problem: "Grainy Print",
      category: "print-quality",
      severity: "medium",
      cause: [
        "Low print resolution or too few passes.",
        "Wrong ICC profile or poor color management.",
        "Ink limits too low for the media and artwork.",
        "Film or ink combination not laying down smoothly."
      ],
      fix: [
        "Increase pass count or resolution and compare results on the same test image.",
        "Verify the ICC profile matches the exact ink, film, and printer setup.",
        "Check ink limits and total ink settings in the RIP.",
        "Test another known-good film batch if the texture looks inconsistent."
      ]
    },
    {
      id: "c4",
      problem: "Dull Colors",
      category: "print-quality",
      severity: "medium",
      cause: [
        "Low ink density or conservative ink limits.",
        "Old, contaminated, or poorly stored ink.",
        "Wrong ICC profile or wrong color mode in artwork.",
        "Poor white underbase support affecting top color appearance."
      ],
      fix: [
        "Review ink density and total ink limits inside the RIP.",
        "Confirm ink age, storage condition, and proper agitation where required.",
        "Verify ICC profile and make sure artwork is prepared in the expected color workflow.",
        "Check white performance if colors look weak on dark garment transfers."
      ]
    },
    {
      id: "c5",
      problem: "Faded White",
      category: "white-ink",
      severity: "high",
      cause: [
        "White ink settling from poor agitation or circulation.",
        "Partial white nozzle clog.",
        "Low white density setting in the RIP.",
        "Weak ink flow from dampers, lines, or filters."
      ],
      fix: [
        "Agitate white ink as recommended and verify circulation is actually running.",
        "Run a white nozzle check and compare before and after cleaning.",
        "Increase white density only after verifying the nozzles are healthy.",
        "Inspect white ink path for flow restriction, air, or weak circulation performance."
      ]
    },
    {
      id: "c6",
      problem: "White Not Printing",
      category: "white-ink",
      severity: "critical",
      cause: [
        "Severe clog in white channels.",
        "Empty cartridge or no ink delivery.",
        "Air in white lines.",
        "White circulation or pump failure.",
        "Incorrect RIP channel configuration."
      ],
      fix: [
        "Verify the printer is actually being commanded to print white in the RIP.",
        "Check white ink level, line condition, and whether ink is reaching the head.",
        "Run a nozzle check and inspect for total channel loss versus partial loss.",
        "Prime lines if appropriate for your setup and inspect for air intrusion.",
        "Check circulation pump, valves, and white delivery hardware if the issue persists."
      ]
    },
    {
      id: "c7",
      problem: "Weak White",
      category: "white-ink",
      severity: "high",
      cause: [
        "Partial clog or weak white channel output.",
        "Low white density setting.",
        "Settled white ink.",
        "Restricted flow through damper or line."
      ],
      fix: [
        "Check nozzle health first before increasing density.",
        "Agitate white ink and confirm circulation is functioning.",
        "Increase white density only if the printhead is firing correctly.",
        "Inspect dampers, filters, and lines for restriction."
      ]
    },
    {
      id: "c8",
      problem: "Clogged Head",
      category: "hardware",
      severity: "critical",
      cause: [
        "Dried ink in nozzles from idle time or poor maintenance.",
        "Weak cap seal allowing nozzles to dry out.",
        "Dirty capping station or poor suction.",
        "Environmental conditions accelerating dry-out."
      ],
      fix: [
        "Run a controlled cleaning sequence and compare nozzle checks between cycles.",
        "Inspect cap top seal, capping station cleanliness, and suction performance.",
        "Use approved recovery methods like wet capping if appropriate for your machine.",
        "Correct humidity and maintenance habits so the problem does not immediately return."
      ]
    },
    {
      id: "c9",
      problem: "White Settling",
      category: "white-ink",
      severity: "high",
      cause: [
        "Insufficient white ink circulation.",
        "Ink not being agitated often enough.",
        "Long idle periods.",
        "Weak circulation hardware."
      ],
      fix: [
        "Increase white circulation runtime if your machine allows it.",
        "Agitate tanks or bottles as recommended by the ink supplier.",
        "Avoid long idle periods without white maintenance.",
        "Inspect circulation pump and check valves if settling is frequent."
      ]
    },
    {
      id: "c10",
      problem: "Ink Separation",
      category: "print-quality",
      severity: "medium",
      cause: [
        "Incompatible film coating.",
        "Humidity or environment affecting ink laydown.",
        "Excessive ink load on the film.",
        "Ink or film quality inconsistency."
      ],
      fix: [
        "Test a known-good film batch from a reliable supplier.",
        "Reduce ink load slightly and compare laydown behavior.",
        "Check humidity and temperature in the workspace.",
        "Verify the ink and film combination is proven to work together."
      ]
    },
    {
      id: "c11",
      problem: "Overspray",
      category: "print-quality",
      severity: "high",
      cause: [
        "Head height too high causing misting.",
        "Static attraction pulling fine droplets.",
        "Ink mist from unstable jetting.",
        "Environmental dryness increasing static."
      ],
      fix: [
        "Lower head height to the minimum safe gap for your setup.",
        "Increase humidity into a safer range and add anti-static measures if needed.",
        "Inspect nozzle health because unstable nozzles can increase overspray.",
        "Test on a known-good film batch to rule out coating issues."
      ]
    },
    {
      id: "c12",
      problem: "Double Image",
      category: "print-quality",
      severity: "high",
      cause: [
        "Dirty encoder strip causing timing errors.",
        "Media slip or poor film advance control.",
        "Bi-di alignment drift.",
        "Mechanical carriage instability."
      ],
      fix: [
        "Clean encoder strip carefully and inspect for damage.",
        "Check film tension and make sure the media path is stable.",
        "Run bi-directional alignment and confirm the chosen pattern.",
        "Inspect carriage travel if the issue started after impact or service."
      ]
    },
    {
      id: "c13",
      problem: "Blurry Edges",
      category: "print-quality",
      severity: "medium",
      cause: [
        "Head height too high.",
        "Low resolution or too few passes.",
        "Overspray from static or unstable jetting.",
        "Artwork quality or raster softness."
      ],
      fix: [
        "Lower head height safely and retest the same file.",
        "Increase resolution or pass count where appropriate.",
        "Control static and verify nozzle health.",
        "Confirm the artwork itself is sharp at print size."
      ]
    },
    {
      id: "c14",
      problem: "Color Mismatch",
      category: "print-quality",
      severity: "medium",
      cause: [
        "Wrong ICC profile.",
        "Incorrect artwork color mode.",
        "Uncalibrated expectations between screen and print.",
        "Ink, film, or white underbase changes affecting output."
      ],
      fix: [
        "Verify the ICC profile matches the exact printer, ink, and film combination.",
        "Check whether the artwork is RGB or CMYK and whether the RIP expects one over the other.",
        "Print a controlled color target to compare changes accurately.",
        "Standardize your workflow so the same job is prepared the same way each time."
      ]
    },
    {
      id: "c15",
      problem: "ICC Error",
      category: "print-quality",
      severity: "medium",
      cause: [
        "Corrupted or missing ICC profile.",
        "Incorrect RIP profile assignment.",
        "Profile mismatch after updates or config changes."
      ],
      fix: [
        "Reinstall the ICC profile from a trusted source.",
        "Clear RIP cache if the software is using a bad stored state.",
        "Verify the correct profile is assigned in the active print mode.",
        "Retest with a known working mode if available."
      ]
    },
    {
      id: "c16",
      problem: "Powder Not Sticking",
      category: "adhesion",
      severity: "high",
      cause: [
        "Ink drying too much before powder application.",
        "Static repelling powder.",
        "Low ink deposit or weak white laydown.",
        "Film surface issue."
      ],
      fix: [
        "Apply powder within the correct timing window after printing.",
        "Reduce static by correcting humidity and using anti-static measures.",
        "Check whether the print has enough wet ink for powder to grab properly.",
        "Test another film batch if the same job repeatedly resists powder."
      ]
    },
    {
      id: "c17",
      problem: "Patchy Powder",
      category: "adhesion",
      severity: "medium",
      cause: [
        "Uneven powder application.",
        "Film not laying flat.",
        "Static causing powder irregularity.",
        "Wet ink inconsistency across the print."
      ],
      fix: [
        "Apply powder more evenly and remove excess consistently.",
        "Make sure the film is flat and stable before and during powdering.",
        "Address static and environmental dryness.",
        "Check for inconsistent ink laydown if patchiness follows print pattern."
      ]
    },
    {
      id: "c18",
      problem: "Film Oily",
      category: "film",
      severity: "medium",
      cause: [
        "Contaminated film surface.",
        "Manufacturing residue on poor film.",
        "Handling contamination from fingers or environment."
      ],
      fix: [
        "Test a fresh sheet from a different area or new batch.",
        "Avoid touching printable surfaces with bare hands.",
        "Replace questionable film stock if contamination is consistent.",
        "Do not rely on wiping bad film into being good if the coating is poor."
      ]
    },
    {
      id: "c19",
      problem: "Ink Wet Too Long",
      category: "adhesion",
      severity: "medium",
      cause: [
        "High humidity slowing evaporation.",
        "Excessive ink load.",
        "Low pre-heat or weak drying stage.",
        "Film coating behavior."
      ],
      fix: [
        "Lower humidity into a safer range.",
        "Reduce ink load slightly and compare drying speed.",
        "Increase pre-heat or drying support if your workflow uses it.",
        "Test another film if wet-out behavior seems abnormal."
      ]
    },
    {
      id: "c20",
      problem: "Under-Cured Powder",
      category: "adhesion",
      severity: "critical",
      cause: [
        "Cure temperature too low.",
        "Cure time too short.",
        "Belt speed too fast.",
        "Uneven oven performance."
      ],
      fix: [
        "Verify actual cure temperature rather than trusting the display blindly.",
        "Slow belt speed or increase dwell time until cure is complete.",
        "Look for the correct cured appearance and feel for your powder system.",
        "Check for uneven heating across the curing area."
      ]
    },
    {
      id: "c21",
      problem: "Over-Cured Powder",
      category: "adhesion",
      severity: "high",
      cause: [
        "Excessive cure temperature.",
        "Cure time too long.",
        "Slow belt speed.",
        "Heat concentration too aggressive."
      ],
      fix: [
        "Reduce cure temperature in controlled small steps.",
        "Shorten dwell time or increase belt speed.",
        "Compare feel and stretch against a known good sample.",
        "Watch for brittleness, discoloration, or reduced adhesion performance."
      ]
    },
    {
      id: "c22",
      problem: "Transfer Peeling",
      category: "adhesion",
      severity: "critical",
      cause: [
        "Under-cured adhesive powder.",
        "Incorrect press temperature, time, or pressure.",
        "Moisture in garment.",
        "Wrong peel timing for the film type."
      ],
      fix: [
        "Verify the transfer was fully cured before pressing.",
        "Confirm the press is actually reaching target temperature and pressure.",
        "Pre-press the garment to remove moisture and flatten the surface.",
        "Follow the correct hot, warm, or cold peel behavior for that exact film."
      ]
    },
    {
      id: "c23",
      problem: "Cracking Print",
      category: "adhesion",
      severity: "high",
      cause: [
        "Over-cured adhesive.",
        "Low ink deposit or weak film build.",
        "Transfer too brittle from process imbalance.",
        "Poor stretch compatibility with the garment or application."
      ],
      fix: [
        "Reduce cure intensity if the transfer feels overly rigid.",
        "Check ink density and white support where the print feels too thin.",
        "Test stretch after process adjustments instead of relying on one visual check.",
        "Match your application settings to the garment type more carefully."
      ]
    },
    {
      id: "c24",
      problem: "Wash Failure",
      category: "adhesion",
      severity: "critical",
      cause: [
        "Poor powder cure.",
        "Weak bonding during pressing.",
        "Low-quality adhesive powder.",
        "Incorrect garment application settings."
      ],
      fix: [
        "Verify cure quality before pressing and before large production runs.",
        "Check press temperature, time, and pressure with real measurement tools.",
        "Test a known good powder if current stock is questionable.",
        "Run controlled wash tests whenever you change film, powder, ink, or press behavior."
      ]
    },
    {
      id: "c25",
      problem: "Head Strike",
      category: "hardware",
      severity: "critical",
      cause: [
        "Head height too low.",
        "Warped or lifted film.",
        "Media path instability.",
        "Debris or curl in the print path."
      ],
      fix: [
        "Stop printing immediately to prevent worse damage.",
        "Raise head height to a safe level and inspect the media path.",
        "Flatten or replace lifted film and confirm tension is stable.",
        "Inspect the printhead, carriage path, and alignment before resuming production."
      ]
    },
    {
      id: "c26",
      problem: "Ink Starvation",
      category: "hardware",
      severity: "high",
      cause: [
        "Low or empty cartridge.",
        "Air in lines.",
        "Weak damper or restricted filter.",
        "Poor venting or flow issue."
      ],
      fix: [
        "Verify ink supply and whether the channel is truly drawing ink.",
        "Inspect lines for air gaps or inconsistent flow.",
        "Check dampers and filters for restriction.",
        "Prime or recover ink flow according to your machine’s safe procedure."
      ]
    },
    {
      id: "c27",
      problem: "Pump Failure",
      category: "hardware",
      severity: "high",
      cause: [
        "Worn or weak pump diaphragm.",
        "Electrical connection issue.",
        "Blocked lines or reduced suction path.",
        "General mechanical pump wear."
      ],
      fix: [
        "Inspect electrical connections and whether the pump is cycling correctly.",
        "Check suction path for blockage or leakage.",
        "Compare maintenance performance before and after pump activation.",
        "Replace the pump if it cannot maintain proper suction."
      ]
    },
    {
      id: "c28",
      problem: "Capping Station",
      category: "hardware",
      severity: "high",
      cause: [
        "Worn cap seal.",
        "Dried ink contamination.",
        "Poor seating against the head.",
        "Weak suction through the cap top."
      ],
      fix: [
        "Clean the capping station thoroughly and remove dried residue.",
        "Inspect the cap seal for deformation or wear.",
        "Confirm the head parks and seals correctly on the cap.",
        "Replace worn components if suction and sealing remain weak."
      ]
    },
    {
      id: "c29",
      problem: "Wiper Issues",
      category: "hardware",
      severity: "medium",
      cause: [
        "Worn wiper blade.",
        "Dried ink buildup on wiper.",
        "Poor cleaning contact during maintenance."
      ],
      fix: [
        "Clean the wiper thoroughly.",
        "Replace the blade if it is nicked, curled, or hardened.",
        "Inspect whether maintenance cycles are leaving excess ink on the head face."
      ]
    },
    {
      id: "c30",
      problem: "Encoder Problems",
      category: "hardware",
      severity: "high",
      cause: [
        "Dirty encoder strip.",
        "Scratched or damaged strip.",
        "Reader contamination.",
        "Carriage timing instability."
      ],
      fix: [
        "Clean the encoder strip carefully using approved technique and materials.",
        "Inspect the strip for scratches, haze, or damage.",
        "Check the encoder reader area for contamination.",
        "Replace damaged parts if cleaning does not restore stable operation."
      ]
    },
    {
      id: "c31",
      problem: "Air in Lines",
      category: "hardware",
      severity: "high",
      cause: [
        "Low ink supply.",
        "Loose fitting or leak.",
        "Poor priming or recent cartridge change.",
        "Flow instability in the supply path."
      ],
      fix: [
        "Refill or stabilize the ink supply first.",
        "Inspect fittings and connections for leakage or looseness.",
        "Prime safely according to your machine’s procedure.",
        "Monitor whether bubbles reappear, which suggests an upstream leak or flow issue."
      ]
    },
    {
      id: "c32",
      problem: "Damper Failure",
      category: "hardware",
      severity: "high",
      cause: [
        "Aged membrane inside damper.",
        "Restricted flow through the damper.",
        "Air leakage or poor sealing.",
        "Contamination buildup."
      ],
      fix: [
        "Replace the dampers if flow is weak or inconsistent.",
        "Inspect connected lines and fittings during replacement.",
        "Compare nozzle check stability before and after the change.",
        "Use preventive maintenance intervals instead of waiting for full failure."
      ]
    },
    {
      id: "c33",
      problem: "Ink Not Sticking to Film",
      category: "film",
      severity: "high",
      cause: [
        "Poor film coating.",
        "Film contamination.",
        "Incompatible film and ink combination.",
        "Static or environmental interference."
      ],
      fix: [
        "Test a known-good film from a trusted supplier.",
        "Avoid handling the printable side carelessly.",
        "Compare behavior across film batches to isolate coating issues.",
        "Control static and environment if adhesion changes with weather."
      ]
    },
    {
      id: "c34",
      problem: "Static Electricity",
      category: "film",
      severity: "medium",
      cause: [
        "Low humidity.",
        "Dry film handling environment.",
        "Lack of anti-static control.",
        "Material friction building charge."
      ],
      fix: [
        "Raise humidity into a safer working range.",
        "Use anti-static tools or ionizing solutions where needed.",
        "Handle and store film in a way that reduces friction and charge buildup.",
        "Retest overspray or powder issues after correcting the room environment."
      ]
    },
    {
      id: "c35",
      problem: "Film Curling",
      category: "film",
      severity: "medium",
      cause: [
        "Humidity imbalance.",
        "Improper storage.",
        "Film memory from the roll.",
        "Heat exposure or poor environment."
      ],
      fix: [
        "Store film flat or in stable controlled conditions.",
        "Reduce humidity swings and protect film from heat.",
        "Allow film to acclimate to the room before use when needed.",
        "Reject badly distorted rolls rather than forcing unstable media through production."
      ]
    },
    {
      id: "c36",
      problem: "Cheap Film Issues",
      category: "film",
      severity: "medium",
      cause: [
        "Poor coating consistency.",
        "Weak release behavior.",
        "Unstable powder response.",
        "Low manufacturing quality control."
      ],
      fix: [
        "Compare results against a known reliable film immediately.",
        "Do not waste time over-tuning around obviously poor media.",
        "Document which suppliers and batches perform consistently.",
        "Use higher-quality film when repeatability matters more than tiny material savings."
      ]
    },
    {
      id: "c37",
      problem: "Film Storage",
      category: "film",
      severity: "low",
      cause: [
        "Exposure to heat or humidity.",
        "Poor stacking or roll storage.",
        "Contamination during storage.",
        "Lack of environmental control."
      ],
      fix: [
        "Store film in stable temperature and humidity.",
        "Keep media protected from dust, sunlight, and excessive moisture.",
        "Use clean storage practices and avoid crushing or bending stock.",
        "Rotate inventory so older film is not forgotten and degraded."
      ]
    },
    {
      id: "c38",
      problem: "Press Not Bonding",
      category: "heat-press",
      severity: "critical",
      cause: [
        "Press temperature too low.",
        "Moisture in garment.",
        "Incorrect pressure.",
        "Under-cured transfer."
      ],
      fix: [
        "Verify actual platen temperature with an external measurement tool.",
        "Pre-press the garment to remove moisture and flatten fibers.",
        "Check and adjust pressure across the full platen.",
        "Confirm the transfer itself was cured correctly before pressing."
      ]
    },
    {
      id: "c39",
      problem: "Press Ghosting",
      category: "heat-press",
      severity: "medium",
      cause: [
        "Film shifting during peel or repress.",
        "Garment movement.",
        "Poor alignment control.",
        "Premature movement while transfer is unstable."
      ],
      fix: [
        "Tape the transfer securely when needed.",
        "Use controlled peel technique and avoid shifting during removal.",
        "Stabilize the garment before repressing.",
        "Reduce unnecessary movement between press steps."
      ]
    },
    {
      id: "c40",
      problem: "Press Marks",
      category: "heat-press",
      severity: "medium",
      cause: [
        "Excessive pressure.",
        "Hard edge from platen or cover sheet.",
        "Sensitive garment fabric.",
        "Over-pressing finished area."
      ],
      fix: [
        "Reduce pressure to the minimum effective level.",
        "Use protective sheets or pillows when appropriate.",
        "Adjust technique for delicate or mark-prone garments.",
        "Test short controlled press cycles on sensitive fabrics."
      ]
    },
    {
      id: "c41",
      problem: "Wrong Press Temp",
      category: "heat-press",
      severity: "high",
      cause: [
        "Sensor drift.",
        "Displayed temp not matching actual platen temp.",
        "Uneven platen heating."
      ],
      fix: [
        "Check platen temperature with a reliable external tool.",
        "Calibrate or service the press if the display is inaccurate.",
        "Test multiple platen areas to find hot or cold spots.",
        "Do not trust the display blindly when bond issues exist."
      ]
    },
    {
      id: "c42",
      problem: "Pressure Problems",
      category: "heat-press",
      severity: "high",
      cause: [
        "Uneven pressure distribution.",
        "Warped or inconsistent platen contact.",
        "Garment seams or thickness variation.",
        "Incorrect press setup."
      ],
      fix: [
        "Perform a pressure consistency check across the platen.",
        "Adjust pressure and compensate for garment thickness.",
        "Use pillows or pads where seams and edges distort contact.",
        "Retest center and edge adhesion after adjustments."
      ]
    },
    {
      id: "c43",
      problem: "Low Humidity",
      category: "environment",
      severity: "medium",
      cause: [
        "Dry climate or indoor air.",
        "HVAC drying the room excessively.",
        "Lack of humidification."
      ],
      fix: [
        "Add humidification and monitor the room consistently.",
        "Aim for a safer operating humidity range for DTF stability.",
        "Watch whether static, overspray, and powder issues improve as humidity normalizes."
      ]
    },
    {
      id: "c44",
      problem: "High Humidity",
      category: "environment",
      severity: "medium",
      cause: [
        "Wet room environment.",
        "Poor dehumidification.",
        "Seasonal moisture swings."
      ],
      fix: [
        "Use dehumidification and monitor the room rather than guessing.",
        "Protect film, powder, and garments from moisture exposure.",
        "Retest cure and powder behavior after stabilizing the room."
      ]
    },
    {
      id: "c45",
      problem: "Temperature Issues",
      category: "environment",
      severity: "low",
      cause: [
        "Poor climate control.",
        "Heat swings in the workspace.",
        "Cold starts affecting material behavior."
      ],
      fix: [
        "Maintain a more stable working temperature.",
        "Avoid large room swings between storage and production.",
        "Let materials acclimate when moving from very different environments."
      ]
    },
    {
      id: "c46",
      problem: "Dust Problems",
      category: "environment",
      severity: "medium",
      cause: [
        "Dirty workspace.",
        "Poor air filtration.",
        "Open material storage.",
        "Infrequent cleaning around printer and press area."
      ],
      fix: [
        "Clean production areas on a repeatable schedule.",
        "Improve air filtration if dust is chronic.",
        "Protect film and garments from open contamination.",
        "Watch for dust-related print defects and not just obvious surface dirt."
      ]
    },
    {
      id: "c47",
      problem: "Mixing Ink Brands",
      category: "hardware",
      severity: "critical",
      cause: [
        "Incompatible chemistry between ink systems.",
        "Pigment and binder mismatch.",
        "Reaction inside lines, dampers, or head."
      ],
      fix: [
        "Stop and do not continue printing mixed chemistry through the system.",
        "Flush the system correctly if changing ink systems is truly required.",
        "Use one proven ink set matched to your machine and profile workflow.",
        "Do not assume similar-looking inks are chemically compatible."
      ]
    },
    {
      id: "c48",
      problem: "Head Height Wrong",
      category: "hardware",
      severity: "high",
      cause: [
        "Incorrect platen gap.",
        "Head too high causing misting and blur.",
        "Head too low causing strikes and instability.",
        "Media thickness or flatness not being accounted for."
      ],
      fix: [
        "Set the minimum safe head gap for your machine and media.",
        "Lower overly high head settings to reduce misting and improve edge definition.",
        "Raise the head if film instability or strike risk exists.",
        "Confirm head height after any media or workflow change that affects print path geometry."
      ]
    },
    {
      id: "c49",
      problem: "RIP Settings",
      category: "print-quality",
      severity: "medium",
      cause: [
        "Wrong resolution or pass count.",
        "Incorrect ICC profile.",
        "Improper ink limits or white settings.",
        "Mode mismatch for the job type."
      ],
      fix: [
        "Review the active print mode rather than assuming the saved preset is correct.",
        "Verify profile, resolution, pass count, and ink settings against a known good baseline.",
        "Standardize working presets so successful jobs are easier to repeat.",
        "Retest on a controlled sample file after making changes."
      ]
    },
    {
      id: "c50",
      problem: "Power Outage",
      category: "hardware",
      severity: "high",
      cause: [
        "Unexpected shutdown interrupting print or maintenance cycle.",
        "Head left uncapped or dirty after loss of power.",
        "System not recovered correctly before restart."
      ],
      fix: [
        "Inspect printer condition before immediately resuming work.",
        "Run the necessary maintenance and nozzle checks before returning to production.",
        "Check whether the head parked and sealed correctly during the outage.",
        "Use backup power protection where possible if outages are common."
      ]
    },
    {
      id: "c51",
      problem: "Nozzle Check Fail",
      category: "hardware",
      severity: "high",
      cause: [
        "Partial clog.",
        "Air in lines.",
        "Weak cap seal or suction.",
        "Flow instability through dampers or filters."
      ],
      fix: [
        "Compare nozzle checks before and after each recovery step instead of guessing.",
        "Check capping performance and whether the head is staying wet enough between prints.",
        "Inspect for air or flow issues upstream in the ink path.",
        "Use controlled recovery rather than endless aggressive cleaning."
      ]
    },
    {
      id: "c52",
      problem: "Bi-Di Alignment",
      category: "print-quality",
      severity: "medium",
      cause: [
        "Alignment drift over time.",
        "Mechanical impact or service change.",
        "Encoder-related inconsistency."
      ],
      fix: [
        "Run the full bi-directional alignment routine.",
        "Choose the best pattern carefully, not quickly.",
        "Retest text and fine lines after adjustment.",
        "Check encoder cleanliness if alignment will not stabilize."
      ]
    },
    {
      id: "c53",
      problem: "White Circulation Fail",
      category: "white-ink",
      severity: "critical",
      cause: [
        "Circulation pump failure.",
        "Valve blockage or sticking.",
        "Flow restriction in white circuit.",
        "Electrical or control issue."
      ],
      fix: [
        "Confirm whether circulation is truly running rather than assuming by sound alone.",
        "Inspect pump, valves, and white path for blockage or weak movement.",
        "Check whether white settling has worsened due to the failure.",
        "Repair the hardware issue before trying to compensate with settings."
      ]
    },
    {
      id: "c54",
      problem: "Yellowed White",
      category: "white-ink",
      severity: "medium",
      cause: [
        "Over-cure heat exposure.",
        "Aged or contaminated white ink.",
        "Storage or environment affecting white chemistry."
      ],
      fix: [
        "Reduce cure intensity if yellowing appears heat-related.",
        "Replace suspect white ink if age or contamination is a factor.",
        "Check storage practices and environmental conditions.",
        "Compare new white output against an older sample to confirm improvement."
      ]
    },
    {
      id: "c55",
      problem: "Speckled White",
      category: "white-ink",
      severity: "high",
      cause: [
        "White contamination.",
        "Partial nozzle instability.",
        "Settled pigment not flowing evenly.",
        "Film surface irregularity."
      ],
      fix: [
        "Run a white nozzle check and verify consistency.",
        "Agitate and circulate white properly before retesting.",
        "Inspect for contamination in ink path or supply.",
        "Test on a known-good film to isolate whether the issue follows the media."
      ]
    },
    {
      id: "c56",
      problem: "Ink Drips",
      category: "hardware",
      severity: "high",
      cause: [
        "Excess ink on head face.",
        "Wiper not cleaning effectively.",
        "Ink load too high in certain conditions.",
        "Mechanical maintenance issue."
      ],
      fix: [
        "Inspect head face and maintenance station condition.",
        "Replace or clean the wiper if it is failing to manage excess ink.",
        "Reduce density carefully if dripping is linked to over-wet printing.",
        "Check for repeated contamination after cleaning cycles."
      ]
    },
    {
      id: "c57",
      problem: "Film Static",
      category: "film",
      severity: "medium",
      cause: [
        "Low humidity.",
        "Dry film handling conditions.",
        "Lack of anti-static control."
      ],
      fix: [
        "Raise humidity into a safer range.",
        "Use anti-static tinsel, ionization, or similar tools where appropriate.",
        "Retest print clarity and powder behavior after environment correction."
      ]
    },
    {
      id: "c58",
      problem: "Powder Clumping",
      category: "adhesion",
      severity: "medium",
      cause: [
        "Moisture in powder.",
        "Poor storage conditions.",
        "Old or contaminated powder."
      ],
      fix: [
        "Replace compromised powder with dry known-good stock.",
        "Store powder in airtight low-moisture conditions.",
        "Do not keep using clumped powder expecting consistent cure results."
      ]
    },
    {
      id: "c59",
      problem: "Transfer Too Thick",
      category: "adhesion",
      severity: "medium",
      cause: [
        "Too much adhesive powder applied.",
        "Excessive ink and powder combination.",
        "Heavy hand during powder application."
      ],
      fix: [
        "Apply a thinner, more even powder coat.",
        "Remove excess consistently before curing.",
        "Check ink load if the print is excessively wet and grabbing too much powder."
      ]
    },
    {
      id: "c60",
      problem: "Transfer Too Thin",
      category: "adhesion",
      severity: "medium",
      cause: [
        "Insufficient powder coverage.",
        "Weak ink deposit to hold powder.",
        "Inconsistent powder application."
      ],
      fix: [
        "Increase powder coverage evenly across the print.",
        "Check whether the print has enough wet ink to hold adhesive.",
        "Review application consistency rather than assuming cure is the only issue."
      ]
    },
    {
      id: "c61",
      problem: "Edge Lift",
      category: "adhesion",
      severity: "high",
      cause: [
        "Under-cure.",
        "Insufficient edge pressure.",
        "Poor peel timing.",
        "Moisture or garment texture affecting bond."
      ],
      fix: [
        "Verify cure fully before blaming press alone.",
        "Check edge pressure and platen consistency.",
        "Use correct peel timing for the film type.",
        "Retest with garment pre-press and better moisture control."
      ]
    },
    {
      id: "c62",
      problem: "Center Lift",
      category: "adhesion",
      severity: "high",
      cause: [
        "Uneven pressure across the center.",
        "Platen inconsistency.",
        "Garment seam or thickness issue.",
        "Insufficient bonding in the middle of the press area."
      ],
      fix: [
        "Check platen pressure across the full pressing area.",
        "Use a pressing pillow or support solution where needed.",
        "Retest adhesion at center versus edges after adjustment.",
        "Confirm cure quality so the transfer is capable of bonding properly."
      ]
    },
    {
      id: "c63",
      problem: "Scorched Garment",
      category: "heat-press",
      severity: "high",
      cause: [
        "Temperature too high.",
        "Press dwell too long.",
        "Garment fabric too heat-sensitive.",
        "Poor use of protective cover materials."
      ],
      fix: [
        "Lower press temperature in small controlled steps.",
        "Shorten press time where appropriate.",
        "Use protective sheets if the garment is prone to scorching or shine.",
        "Test application settings specifically for sensitive garments."
      ]
    },
    {
      id: "c64",
      problem: "Cold Peel Issues",
      category: "heat-press",
      severity: "medium",
      cause: [
        "Peeling too early.",
        "Transfer not fully cooled.",
        "Film type expectations not being followed."
      ],
      fix: [
        "Allow the transfer to cool completely before peeling.",
        "Confirm the film is truly a cold peel product.",
        "Retest peel timing after verifying cure and press settings."
      ]
    },
    {
      id: "c65",
      problem: "Hot Peel Issues",
      category: "heat-press",
      severity: "medium",
      cause: [
        "Waiting too long to peel.",
        "Incorrect peel technique.",
        "Film type mismatch."
      ],
      fix: [
        "Peel immediately or within the proper hot-peel window.",
        "Use smooth controlled peel technique.",
        "Confirm the film is designed for hot peel behavior."
      ]
    },
    {
      id: "c66",
      problem: "Print Head Noise",
      category: "hardware",
      severity: "high",
      cause: [
        "Mechanical obstruction.",
        "Carriage issue.",
        "Head height problem.",
        "Recent strike or hardware wear."
      ],
      fix: [
        "Stop and inspect for obstructions before forcing more prints.",
        "Check carriage path, head clearance, and any sign of impact.",
        "Listen for whether the noise is repeatable in the same area.",
        "Service the mechanical issue before continuing production."
      ]
    },
    {
      id: "c67",
      problem: "Carriage Stuck",
      category: "hardware",
      severity: "critical",
      cause: [
        "Physical obstruction.",
        "Belt or motor issue.",
        "Rail contamination.",
        "Sensor or position problem."
      ],
      fix: [
        "Clear any obstruction immediately and carefully.",
        "Inspect belt tension and carriage travel path.",
        "Clean rails or motion path if contamination is present.",
        "Do not force movement until the cause is understood."
      ]
    },
    {
      id: "c68",
      problem: "Film Feed Error",
      category: "film",
      severity: "high",
      cause: [
        "Tension issue.",
        "Dirty or weak sensor.",
        "Roller contamination.",
        "Film tracking instability."
      ],
      fix: [
        "Check rollers, sensor condition, and media path alignment.",
        "Correct film tension so feed is stable but not excessive.",
        "Clean sensors and rollers if feed errors repeat.",
        "Test with a flatter or known-good film roll."
      ]
    },
    {
      id: "c69",
      problem: "Paper Jam",
      category: "film",
      severity: "high",
      cause: [
        "Misfeed.",
        "Film curl or lift.",
        "Obstruction in path.",
        "Poor loading or tension."
      ],
      fix: [
        "Clear the jam fully before restarting.",
        "Inspect for film curl, lift, or damaged edges.",
        "Reload media carefully and confirm path stability.",
        "Check whether head height or path obstructions contributed."
      ]
    },
    {
      id: "c70",
      problem: "USB Connection Lost",
      category: "hardware",
      severity: "medium",
      cause: [
        "Bad USB cable.",
        "Loose connection.",
        "Driver instability.",
        "Interference or PC-side issue."
      ],
      fix: [
        "Check and reseat the cable connection first.",
        "Replace questionable cables with a known-good option.",
        "Reinstall or verify the correct driver.",
        "Reduce other PC-side instability if the issue is recurring."
      ]
    },
    {
      id: "c71",
      problem: "RIP Crash",
      category: "print-quality",
      severity: "medium",
      cause: [
        "Software instability.",
        "Corrupt cache or temp files.",
        "Memory overload.",
        "Bad preset or config state."
      ],
      fix: [
        "Restart the RIP and clear cache if supported.",
        "Update software if the problem is version-related.",
        "Reduce oversized or problematic jobs during testing.",
        "Retest with a known working preset."
      ]
    },
    {
      id: "c72",
      problem: "Print Queue Stuck",
      category: "print-quality",
      severity: "medium",
      cause: [
        "Spooler issue.",
        "Connection interruption.",
        "RIP queue corruption.",
        "Job conflict."
      ],
      fix: [
        "Clear the queue and restart the spooler if needed.",
        "Check communication between RIP and printer.",
        "Resend only the necessary job after cleanup.",
        "Watch for repeated queue failures that suggest deeper software instability."
      ]
    },
    {
      id: "c73",
      problem: "Wrong Color Output",
      category: "print-quality",
      severity: "medium",
      cause: [
        "Wrong ICC profile.",
        "CMYK versus RGB mismatch.",
        "Mode mismatch inside RIP.",
        "Inconsistent workflow preparation."
      ],
      fix: [
        "Verify ICC and print mode first.",
        "Confirm artwork color mode and preparation method.",
        "Print a controlled comparison test rather than guessing from one live order.",
        "Standardize workflow from design export to RIP import."
      ]
    },
    {
      id: "c74",
      problem: "Slow Print Speed",
      category: "print-quality",
      severity: "low",
      cause: [
        "High pass count or high resolution.",
        "USB or communication bottleneck.",
        "Inefficient RIP settings.",
        "Machine operating in a slower quality mode."
      ],
      fix: [
        "Check whether the selected print mode is slower than necessary for the job.",
        "Reduce passes only if quality remains acceptable.",
        "Verify data connection stability and cable health.",
        "Use faster modes strategically instead of using max quality on everything."
      ]
    },
    {
      id: "c75",
      problem: "Incomplete Print",
      category: "print-quality",
      severity: "high",
      cause: [
        "Connection interruption.",
        "Buffer or spool issue.",
        "RIP failure during send.",
        "Job file instability."
      ],
      fix: [
        "Check connection stability first.",
        "Resend the job after clearing queue and RIP issues.",
        "Reduce file complexity temporarily if testing a suspect file.",
        "Watch whether the failure happens at the same point every time."
      ]
    },
    {
      id: "c76",
      problem: "Lines in Solid Areas",
      category: "print-quality",
      severity: "high",
      cause: [
        "Nozzle inconsistency.",
        "Dithering method not matching the artwork well.",
        "Low pass count or weak coverage.",
        "Mechanical or feed inconsistency."
      ],
      fix: [
        "Run a nozzle check before chasing software settings.",
        "Test a different dithering method if the RIP allows it.",
        "Increase pass count or adjust density for better solid fill.",
        "Compare whether the lines follow print direction or media movement."
      ]
    },
    {
      id: "c77",
      problem: "Mottled Print",
      category: "print-quality",
      severity: "medium",
      cause: [
        "Ink inconsistency.",
        "Film coating issue.",
        "Drying irregularity.",
        "Profile or laydown mismatch."
      ],
      fix: [
        "Test a new ink batch or known-good media.",
        "Compare output on another film type if possible.",
        "Check whether humidity or drying stage is affecting surface uniformity.",
        "Review the print mode and laydown settings."
      ]
    },
    {
      id: "c78",
      problem: "Pinholes in White",
      category: "white-ink",
      severity: "high",
      cause: [
        "Partial white nozzle clog.",
        "Low white density.",
        "Air or weak flow in white channel.",
        "Film surface issue."
      ],
      fix: [
        "Run a white nozzle check and inspect for missing jets.",
        "Increase white density only after confirming nozzle health.",
        "Check lines and dampers for weak delivery or air.",
        "Test on a known-good film to rule out coating behavior."
      ]
    },
    {
      id: "c79",
      problem: "White Streaks",
      category: "white-ink",
      severity: "high",
      cause: [
        "Missing white nozzles.",
        "Air in white system.",
        "Weak circulation or poor flow stability."
      ],
      fix: [
        "Run a nozzle check focused on white output.",
        "Recover flow and inspect for air in the white path.",
        "Confirm circulation and agitation practices are strong enough to support stable output."
      ]
    },
    {
      id: "c80",
      problem: "Color Streaks",
      category: "print-quality",
      severity: "high",
      cause: [
        "Color-channel clog.",
        "Weak flow on one color.",
        "Air or damper instability.",
        "Mechanical or alignment issue exaggerating nozzle loss."
      ],
      fix: [
        "Identify which color channel is affected through nozzle checks.",
        "Recover that channel using controlled cleaning and flow inspection.",
        "Inspect damper, filter, and line stability on the affected color.",
        "Retest solids after recovery instead of judging only from mixed artwork."
      ]
    },
    {
      id: "c81",
      problem: "Uneven White",
      category: "white-ink",
      severity: "high",
      cause: [
        "Head alignment issue.",
        "Settled white ink.",
        "Weak white flow.",
        "Inconsistent white density or mode."
      ],
      fix: [
        "Check white nozzle health and alignment first.",
        "Agitate and circulate white properly.",
        "Inspect white path components for restriction.",
        "Verify the RIP is using the intended white mode and density."
      ]
    },
    {
      id: "c82",
      problem: "Spotty Coverage",
      category: "print-quality",
      severity: "high",
      cause: [
        "Nozzle inconsistency.",
        "Ink quality or delivery issue.",
        "Film coating inconsistency.",
        "Low density or weak laydown."
      ],
      fix: [
        "Run a nozzle check and compare output to the print defect.",
        "Inspect ink condition and supply stability.",
        "Test a known-good film batch.",
        "Review density and print mode if coverage is generally weak."
      ]
    },
    {
      id: "c83",
      problem: "Film Marks",
      category: "film",
      severity: "medium",
      cause: [
        "Dirty platen.",
        "Dirty rollers or contact surfaces.",
        "Debris in media path.",
        "Handling contamination."
      ],
      fix: [
        "Clean platen and media path thoroughly.",
        "Inspect rollers and contact surfaces for residue.",
        "Retest on a fresh sheet after cleaning.",
        "Protect printable surfaces from unnecessary handling."
      ]
    },
    {
      id: "c84",
      problem: "Roller Marks",
      category: "film",
      severity: "medium",
      cause: [
        "Roller contamination.",
        "Excessive pressure or contact.",
        "Debris on feed path.",
        "Media instability causing unwanted roller interaction."
      ],
      fix: [
        "Clean the rollers thoroughly.",
        "Inspect roller pressure or contact behavior if adjustable.",
        "Remove debris from the media path.",
        "Check whether film curl or poor feed stability is causing repeated contact marks."
      ]
    }
  ]
};